"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Calendar, Tag, User } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="h-12 w-3/4 animate-pulse rounded-lg bg-muted/30" />
        <div className="mt-8 h-96 w-full animate-pulse rounded-xl bg-muted/30" />
        <div className="mt-8 space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-6 w-full animate-pulse rounded bg-muted/30" />
          ))}
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12 text-center">
        <p className="text-muted-foreground">Post not found</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl px-6 py-12">
      {}
      <h1 className="text-4xl font-bold leading-tight text-foreground md:text-5xl">
        {post.title}
      </h1>

      {}
      <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
        {post.date && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4" />
            <time dateTime={post.date}>
              {new Date(post.date).toLocaleDateString("fr-FR", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
          </div>
        )}
        {post.author && (
          <div className="flex items-center gap-2">
            <User className="size-4" />
            <span>{post.author.name}</span>
          </div>
        )}
      </div>

      <Separator className="my-8" />

      {}
      {post.featuredImage && (
        <div className="my-8 overflow-hidden rounded-xl shadow-medium">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      {}
      {post.content && (
        <div
          className="prose prose-lg max-w-none text-foreground prose-headings:font-display prose-headings:text-foreground prose-p:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-ul:text-foreground prose-ol:text-foreground"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {(post.categories || post.tags) && (
        <div className="mt-12 space-y-4">
          {post.categories && post.categories.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="size-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Catégories:</span>
              {post.categories.map((category, i) => (
                <span
                  key={i}
                  className="rounded-md bg-primary/10 px-3 py-1 text-sm text-primary"
                >
                  {category.name}
                </span>
              ))}
            </div>
          )}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="size-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Tags:</span>
              {post.tags.map((tag, i) => (
                <span
                  key={i}
                  className="rounded-md bg-secondary/10 px-3 py-1 text-sm text-secondary"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <Separator className="my-12" />

      {}
      {post.author && (
        <Card className="border-border bg-muted/30">
          <CardContent className="flex items-start gap-4">
            <Avatar className="size-16">
              {post.author.avatar && post.author.avatar["96"] && (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-primary text-foreground-primary">
                {post.author.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {post.author.name}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Auteur de cet article
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-64 animate-pulse rounded-xl bg-muted/30" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="card hover-lift border-border">
          {post.featuredImage && (
            <div className="overflow-hidden rounded-t-xl">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-48 w-full object-cover transition-transform duration-[var(--duration-medium)] hover:scale-105"
              />
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-lg">
              {post.link ? (
                <Link to={post.link} className="hover:text-primary transition-colors">
                  {post.title}
                </Link>
              ) : (
                post.title
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {post.excerpt && (
              <div
                className="line-clamp-3 text-sm text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: post.excerpt }}
              />
            )}
            {post.link && (
              <Link
                to={post.link}
                className="mt-4 inline-block text-sm font-semibold text-primary hover:underline"
              >
                Lire la suite →
              </Link>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplate() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <div className="bg-background">
      {}
      <PostsProvider wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>

      {}
      <section className="bg-dark-background py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="mb-8 text-center text-3xl font-bold text-foreground-dark">
            Articles similaires
          </h2>
          <PostsProvider wp_query={{ per_page: 3, order: "desc", orderby: "date", exclude: [currentPostId] }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "_embedded", "featured_media", "_links.wp:featuredmedia"]}>
            <RelatedPostsContent />
          </PostsProvider>
        </div>
      </section>
    </div>
  );
}